import React from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * A simple checkbox component used in the goals and competencies sections.
 * It's a visual representation and not a functional input.
 */
const CheckboxItem = ({ label }) => (
    <div className="flex items-center text-xs text-gray-800">
        <div className="w-3 h-3 border border-black mr-2 flex-shrink-0"></div>
        <span className="leading-tight">{label}</span>
    </div>
);

/**
 * This component renders a single page for Part B of the HPC report.
 * It is now connected to live data and uses ReactMarkdown for rich text.
 */
const PartBDomainPage = ({ domain, assignment }) => {
    // If essential data is missing, render a loading/empty state to avoid errors.
    if (!domain || !assignment) {
        return (
            <div className="report-page bg-white shadow-lg flex items-center justify-center text-gray-500"
                style={{ width: '210mm', height: '297mm', margin: '0 auto' }}>
                <p>Loading Domain Information...</p>
            </div>
        );
    }

    const rubricAbilities = ['Awareness', 'Sensitivity', 'Creativity'];

    return (
        <div
            className="report-page bg-white shadow-lg"
            style={{
                width: '210mm',
                height: '297mm',
                boxSizing: 'border-box',
                margin: '0 auto',
                padding: '15mm 20mm',
                fontFamily: 'Arial, sans-serif',
                color: '#333',
                position: 'relative',
                overflow: 'hidden', // This is crucial for printing, it clips overflow.
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* Page Title */}
            <h1 className="text-center text-[#e36c09] font-bold text-xl mb-2">
                PART B
            </h1>

            {/* Domain Header */}
            <div className="bg-orange-600 text-white py-2 px-4 text-center text-base font-bold">
                Learning Standard: {domain.domain_name}
            </div>

            {/* Main Content Table - Using flex to manage height */}
            <div className="border-2 border-orange-500 flex-grow flex flex-col text-sm min-h-0">
                {/* Curricular Goals Section */}
                <div className="flex border-b-2 border-orange-500">
                    <div
                        className="w-1/4 bg-[#fbd4b4] p-2 font-bold border-r-2 border-orange-500 flex items-center justify-center text-center">
                        Curricular Goals <br />(Can choose one or more)
                    </div>
                    <div className="w-3/4 p-3 bg-[#fde9d9] grid grid-cols-5 gap-x-4 gap-y-2">
                        {(assignment.curricular_goals || []).map((goal, i) => <CheckboxItem key={i} label={goal} />)}
                    </div>
                </div>

                {/* Competencies Section */}
                <div className="flex border-b-2 border-orange-500">
                    <div
                        className="w-1/4 bg-[#fbd4b4] p-2 font-bold border-r-2 border-orange-500 flex items-center justify-center text-center">
                        Competencies <br />(Can choose one or more)
                    </div>
                    <div className="w-3/4 p-3 bg-[#fde9d9]  grid grid-cols-5 gap-x-4 gap-y-2">
                        {(assignment.competencies || []).map((comp, i) => <CheckboxItem key={i} label={comp} />)}
                    </div>
                </div>

                {/* Activity Section with ReactMarkdown and flexible, scrolling height */}
                <div className="flex border-b-2 border-orange-500" style={{ flex: '2 1 0%', minHeight: 0 }}>
                    <div
                        className="w-1/4 bg-[#fbd4b4] p-2 font-bold border-r-2 border-orange-500 flex items-center justify-center">
                        Activity
                    </div>
                    <div className="w-3/4 p-3 bg-[#fde9d9] leading-relaxed overflow-y-auto print:overflow-hidden">
                        <ReactMarkdown
                            components={{
                                h4: ({ node, ...props }) => <h4 className="font-bold text-md mb-2" {...props} />,
                                p: ({ node, ...props }) => <p className="mb-2" {...props} />,
                                ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-2" {...props} />,
                                ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-2" {...props} />,
                                li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
                            }}
                        >
                            {assignment.activity}
                        </ReactMarkdown>
                    </div>
                </div>

                {/* Assessment Questions Section with flexible, scrolling height */}
                <div className="flex" style={{ flex: '1 1 0%', minHeight: 0 }}>
                    <div
                        className="w-1/4 bg-[#fbd4b4] p-2 font-bold border-r-2 border-orange-500 flex items-center justify-center">
                        Assessment Questions
                    </div>
                    <div className="w-3/4 p-3 bg-[#fde9d9] overflow-y-auto print:overflow-hidden">
                        <ul className="list-disc list-inside space-y-2 leading-relaxed">
                            {(assignment.assessment_questions || []).map((q, i) => <li key={i}>{q}</li>)}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Assessment Rubric Section */}
            <div className="mt-6">
                <h2 className="bg-[#e36c09] text-white py-2 px-4 text-center text-base font-bold">
                    ASSESSMENT RUBRIC*
                </h2>
                <table className="w-full border-collapse border-2 border-orange-500 text-sm">
                    <thead>
                        <tr>
                            <th className="w-1/4 border-2 border-orange-500 p-2 bg-[#f89646] font-bold">Abilities</th>
                            <th className="w-1/4 border-2 border-orange-500 p-2 bg-[#f89646] font-bold">Beginner</th>
                            <th className="w-1/4 border-2 border-orange-500 p-2 bg-[#f89646] font-bold">Proficient</th>
                            <th className="w-1/4 border-2 border-orange-500 p-2 bg-[#f89646] font-bold">Advanced</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rubricAbilities.map(abilityName => {
                            // Find the rubric object for the current ability from the API data
                            const rubricData = Array.isArray(assignment.assessment_rubric)
                                ? assignment.assessment_rubric.find(item =>
                                    item.criterion?.toLowerCase().includes(abilityName.toLowerCase()) ||
                                    item.ability === abilityName
                                )
                                : {};

                            // Safely access the properties, providing fallbacks
                            const beginner = rubricData?.beginner || rubricData?.stream || '';
                            const proficient = rubricData?.proficient || rubricData?.mountain || '';
                            const advanced = rubricData?.advanced || rubricData?.sky || '';

                            return (
                                <tr key={abilityName}>
                                    <td className="border-2 border-orange-500 p-2 bg-[#fde9d9] font-bold align-top">{abilityName}</td>
                                    <td className="border-2 border-orange-500 p-2 h-16 align-top text-xs">{beginner}</td>
                                    <td className="border-2 border-orange-500 p-2 h-16 align-top text-xs">{proficient}</td>
                                    <td className="border-2 border-orange-500 p-2 h-16 align-top text-xs">{advanced}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <p className="text-xs italic mt-1 text-gray-700">*Please write the assessment rubric for the performance
                    levels of each ability.</p>
            </div>
        </div>
    );
};

export default PartBDomainPage;
